import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import utils, {
  getHasElementsInArray,
  getUserFullName,
  normalizeEmail,
} from 'utils';
import useQueryParams from 'hooks/useQueryParams';
import { FilterOption } from 'models/Filters';
import { getDistinctOptions } from 'router/subrouters/DataRoom/utils';
import {
  DATA_ROOM_MEMBER_STATUS,
  DataRoomBuySideGroup,
  DataRoomMember,
} from 'router/subrouters/DataRoom/models/DataRoomMember';
import {
  qaRoleTagLabelMap,
  roleTagLabelMap,
} from 'router/subrouters/DataRoom/constants';
import {
  BUY_SIDE_FILTER_PARAMS,
  LAST_SEEN_FILTER,
  LAST_SEEN_FILTER_COPY,
  LAST_SEEN_PREDICATES,
  LastSeenFilter,
  WITHDRAWN_FILTER,
} from '../components/MembersFlatTable/data';

type UseBuySideMemberFiltersProps = {
  members: DataRoomMember[];
  buySideGroups: DataRoomBuySideGroup[];
  memberGroupMap: Record<string, DataRoomBuySideGroup>;
};

export default function useBuySideMemberFilters(
  props: UseBuySideMemberFiltersProps,
) {
  const { members, buySideGroups, memberGroupMap } = props;

  const { t } = useTranslation();

  const { params } = useQueryParams<Record<string, string>>();

  const {
    [BUY_SIDE_FILTER_PARAMS.NAME]: nameParam,
    [BUY_SIDE_FILTER_PARAMS.GROUP]: groupParam,
    [BUY_SIDE_FILTER_PARAMS.FILES_ROLE]: filesRoleParam,
    [BUY_SIDE_FILTER_PARAMS.QA_ROLE]: qaRoleParam,
    [BUY_SIDE_FILTER_PARAMS.STATUS]: statusParam,
    [BUY_SIDE_FILTER_PARAMS.LAST_SEEN]: lastSeenParam,
    [BUY_SIDE_FILTER_PARAMS.WITHDRAWN]: withdrawnParam,
  } = params;

  const filterOptions = useMemo<FilterOption[]>(
    () => [
      {
        value: BUY_SIDE_FILTER_PARAMS.NAME,
        label: t('name'),
        isMultiple: true,
        isSearchable: true,
        // Invited members have no name yet. The table labels them "Invited",
        // but a list of identical options is impossible to pick from, so the
        // filter identifies them by email instead.
        options: members.map(
          ({ _id, isAccepted, firstName, lastName, email }) => ({
            value: _id,
            label:
              (isAccepted
                ? getUserFullName(firstName, lastName)
                : normalizeEmail(email)) || t('invited'),
          }),
        ),
      },
      {
        value: BUY_SIDE_FILTER_PARAMS.GROUP,
        label: t('group'),
        isMultiple: true,
        options: buySideGroups.map(({ _id, name }) => ({
          value: _id,
          label: name,
        })),
      },
      {
        value: BUY_SIDE_FILTER_PARAMS.FILES_ROLE,
        label: t('filesRole'),
        isMultiple: true,
        isSearchable: false,
        options: getDistinctOptions(
          members.map(({ role }) => role),
          (role) => t(roleTagLabelMap[role] ?? role),
        ),
      },
      {
        value: BUY_SIDE_FILTER_PARAMS.QA_ROLE,
        label: t('qaRole'),
        isMultiple: true,
        isSearchable: false,
        options: getDistinctOptions(
          members.map(({ qaRole }) => qaRole),
          (qaRole) => t(qaRoleTagLabelMap[qaRole] ?? qaRole),
        ),
      },
      {
        value: BUY_SIDE_FILTER_PARAMS.STATUS,
        label: t('status'),
        isMultiple: true,
        isSearchable: false,
        shouldSortOptions: false,
        options: [
          { value: DATA_ROOM_MEMBER_STATUS.HOT, label: t('hotLeads') },
          { value: DATA_ROOM_MEMBER_STATUS.COLD, label: t('coldLeads') },
        ],
      },
      {
        // Chronological, not alphabetical: "Last 30 days" must not sort above
        // "Last 7 days", and "All time" belongs at the end.
        value: BUY_SIDE_FILTER_PARAMS.LAST_SEEN,
        label: t('lastSeen'),
        shouldSortOptions: false,
        options: Object.values(LAST_SEEN_FILTER).map((value) => ({
          value,
          label: t(LAST_SEEN_FILTER_COPY[value]),
        })),
      },
      {
        value: BUY_SIDE_FILTER_PARAMS.WITHDRAWN,
        label: t('withdrawnLeads'),
        shouldSortOptions: false,
        options: [
          { value: WITHDRAWN_FILTER.INCLUDE, label: t('include') },
          { value: WITHDRAWN_FILTER.EXCLUDE, label: t('exclude') },
        ],
      },
    ],
    [buySideGroups, members, t],
  );

  const filteredMembers = useMemo(() => {
    const names = utils.mapParamsToArray(nameParam);
    const groups = utils.mapParamsToArray(groupParam);
    const filesRoles = utils.mapParamsToArray(filesRoleParam);
    const qaRoles = utils.mapParamsToArray(qaRoleParam);
    const statuses = utils.mapParamsToArray(statusParam);

    const isLastSeenIn =
      LAST_SEEN_PREDICATES[lastSeenParam as LastSeenFilter] ?? null;

    const includeWithdrawn = withdrawnParam === WITHDRAWN_FILTER.INCLUDE;

    return members.filter((member) => {
      const { _id, role, qaRole, status, lastSeen, isLead, isLeadWithdrawn } =
        member;

      // Withdrawn leads are hidden unless explicitly included.
      if (!includeWithdrawn && isLead && isLeadWithdrawn) return false;

      if (getHasElementsInArray(names) && !names.includes(_id)) return false;

      if (
        getHasElementsInArray(groups) &&
        !groups.includes(memberGroupMap[_id]?._id)
      )
        return false;

      if (getHasElementsInArray(filesRoles) && !filesRoles.includes(role))
        return false;

      if (getHasElementsInArray(qaRoles) && !qaRoles.includes(qaRole))
        return false;

      // Members the BE has not classified drop out once a status is picked.
      if (getHasElementsInArray(statuses) && !statuses.includes(status))
        return false;

      // Same for members who have never been seen, except on "All time" which
      // has no predicate at all.
      if (isLastSeenIn && !(lastSeen && isLastSeenIn(lastSeen))) return false;

      return true;
    });
  }, [
    filesRoleParam,
    groupParam,
    lastSeenParam,
    memberGroupMap,
    members,
    nameParam,
    qaRoleParam,
    statusParam,
    withdrawnParam,
  ]);

  return { filterOptions, filteredMembers };
}
